import React, { useState, useEffect } from 'react';
import MaterialTable from "material-table";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import { WEB_SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { Typography } from '@mui/material';

const Advertisers = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const staticusers = useSelector(state => state.usersdata.staticusers);
  const usersLoading = useSelector(state => state.usersdata.loading);
  const auth = useSelector(state => state.auth);
  const { fetchUsersOnce } = api;
  const dispatch = useDispatch();
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);

  const [data, setData] = useState([]);

  const clickCost = settings && settings.ad_click_cost != null ? parseFloat(settings.ad_click_cost) : 10000;

  function formatAmount(value) {
    const number = parseFloat(value || 0);
    return number.toLocaleString("en-US", {
      minimumFractionDigits: settings ? settings.decimal : 2,
      maximumFractionDigits: settings ? settings.decimal : 2
    });
  }

  const symbolize = (value) =>
    settings && settings.swipe_symbol
      ? formatAmount(value) + " " + (settings ? settings.symbol : '')
      : (settings ? settings.symbol : '') + " " + formatAmount(value);

  useEffect(() => {
    if (auth.profile?.usertype === 'admin') {
      dispatch(fetchUsersOnce());
    }
  }, [dispatch, fetchUsersOnce, auth.profile?.usertype]);

  useEffect(() => {
    if (usersLoading || !staticusers) {
      setData([]);
      return;
    }
    const drivers = staticusers.filter(u => u.usertype === 'driver');
    const rows = drivers.map(d => {
      const adBalance = parseFloat(d.adBalance || 0);
      const servicesLeft = clickCost > 0 ? Math.floor(adBalance / clickCost) : 0;
      const active = d.adActive === true || adBalance >= clickCost;
      return {
        id: d.id,
        driver: [d.firstName, d.lastName].filter(Boolean).join(' ') || d.email || d.id,
        email: d.email || '',
        mobile: d.mobile || '',
        adBalance,
        servicesLeft,
        missedAdServices: parseInt(d.missedAdServices || 0, 10),
        active,
      };
    });
    rows.sort((a, b) => b.adBalance - a.adBalance);
    setData(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticusers, settings, usersLoading]);

  const columns = [
    { title: t('driver_name', { defaultValue: 'Conductor' }), field: 'driver' },
    { title: t('email', { defaultValue: 'Correo' }), field: 'email' },
    { title: t('mobile', { defaultValue: 'Móvil' }), field: 'mobile' },
    { title: t('ad_balance_label', { defaultValue: 'Saldo para impulso' }), field: 'adBalance', render: (r) => symbolize(r.adBalance) },
    { title: t('ad_services_left', { defaultValue: 'Servicios disponibles' }), field: 'servicesLeft', type: 'numeric' },
    {
      title: t('advertisers_status', { defaultValue: 'Estado' }),
      field: 'active',
      render: (r) => (
        <span style={{
          padding: '4px 10px', borderRadius: 12, fontWeight: 700, fontSize: 12,
          color: '#fff', background: r.active ? '#06C167' : '#9A9A9A'
        }}>
          {r.active ? t('ad_active_title', { defaultValue: 'Con impulso' }) : t('advertisers_inactive', { defaultValue: 'Sin impulso' })}
        </span>
      )
    },
    { title: t('advertisers_missed', { defaultValue: 'Servicios perdidos' }), field: 'missedAdServices', type: 'numeric' },
  ];

  const advertisers = data.filter(r => r.active);
  const totalAdBalance = advertisers.reduce((s, r) => s + r.adBalance, 0);
  const tableRows = advertisers;

  return (
    usersLoading ? <CircularLoading /> :
      <ThemeProvider theme={theme}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 14px 0', direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
          <div style={{ background: '#000000', color: '#fff', borderRadius: 12, padding: '14px 20px', minWidth: 200 }}>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{t('advertisers_active', { defaultValue: 'Conductores con impulso' })}</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{advertisers.length}</div>
          </div>
          <div style={{ background: '#06C167', color: '#fff', borderRadius: 12, padding: '14px 20px', minWidth: 200 }}>
            <div style={{ fontSize: 13, opacity: 0.9 }}>{t('advertisers_total_balance', { defaultValue: 'Saldo total para impulso' })}</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{symbolize(totalAdBalance)}</div>
          </div>
          <div style={{ background: '#333333', color: '#fff', borderRadius: 12, padding: '14px 20px', minWidth: 200 }}>
            <div style={{ fontSize: 13, opacity: 0.9 }}>{t('ad_cost_per_service', { defaultValue: 'Costo por servicio' })}</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{symbolize(clickCost)}</div>
          </div>
        </div>
        <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: colors.SHADOW, marginBottom: 8 }}>
          {t('advertisers_hint', { defaultValue: 'Conductores que impulsaron su perfil. El sistema les muestra solicitudes con mayor visibilidad y descuenta el costo por cada servicio que toman.' })}
        </Typography>
        <MaterialTable
          title={t('advertisers_menu', { defaultValue: 'Impulso' })}
          columns={columns}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
            padding: "20px",
          }}
          data={tableRows}
          options={{
            exportButton: true,
            ...TableStyle(settings)
          }}
          localization={localization(t)}
        />
      </ThemeProvider>
  );
}

export default Advertisers;
